/**
 * "Filter By", across the page rather than down the side of it.
 *
 * THE SAME CONTROL t1 PUTS IN ITS LEFT RAIL -- the same sections, the same square checkboxes,
 * 11px labels, right-aligned counts and the same Clear -- laid out horizontally because this page
 * is a wall of cards that wants its full width, where t1's is a grid that does not.
 *
 * IT COLLAPSES, AND WHAT IT SAYS WHEN COLLAPSED IS THE POINT. A band shut while filters are active
 * leaves a list showing a subset with nothing on screen saying so, and the reader concludes there
 * are five documents rather than that they filtered to five. So the header carries the active
 * filters as text whenever it is closed.
 */
import React from "react";
import { Box, Collapse, IconButton, InputBase, Typography } from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

import { DocumentFilters, DocumentStatistics, anyFilterActive } from "../../util/documentFilters";

const TEXT = "#2B2B2B";
const HEADING = "#000000";
const MUTED = "#717171";
const LINK = "#1498D8";
const BORDER = "#E0E0E0";

function Checkbox({ checked }: { checked: boolean }) {
    return (
        <Box sx={{
            width: 13, height: 13, flexShrink: 0, borderRadius: "2px",
            border: `1px solid ${checked ? LINK : "#B5B5B5"}`,
            backgroundColor: checked ? LINK : "#FFFFFF",
            display: "flex", alignItems: "center", justifyContent: "center",
        }}>
            {checked && <Box sx={{ width: 6, height: 6, backgroundColor: "#FFFFFF", borderRadius: "1px" }} />}
        </Box>
    );
}

function FilterRow({ label, title, count, checked, onToggle }: {
    label: string; title?: string; count?: number; checked: boolean; onToggle: () => void;
}) {
    return (
        // THE TITLE CARRIES WHAT THE LABEL CANNOT. A type row reads "SOP"; the type is called
        // "Standard Operating Procedure", which is the thing a reader who does not already know the
        // acronym needs and which will not fit in a row of them.
        <Box onClick={onToggle} title={title}
             sx={{ display: "flex", alignItems: "center", gap: 0.75, cursor: "pointer", py: "2px",
                   "&:hover .lbl": { textDecoration: "underline" } }}>
            <Checkbox checked={checked} />
            <Typography className="lbl" sx={{
                fontSize: 11, lineHeight: 1.5, whiteSpace: "nowrap",
                color: checked ? LINK : TEXT, fontWeight: checked ? 700 : 400,
            }}>{label}</Typography>
            {/* An undefined count renders nothing; a zero renders "0". Collapsing the two would hide
                the useful fact that a status genuinely has no documents. */}
            {count !== undefined && <Typography sx={{ fontSize: 10, color: MUTED, ml: 0.25 }}>{count.toLocaleString()}</Typography>}
        </Box>
    );
}

/** One horizontal section: its heading on the left, its rows flowing and wrapping beside it. */
function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, py: 0.75,
                   borderTop: `1px solid ${BORDER}` }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: HEADING, width: 72,
                              flexShrink: 0, pt: "2px" }}>
                {heading}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", columnGap: 2.5, rowGap: 0.25 }}>
                {children}
            </Box>
        </Box>
    );
}

export default function DocumentFilterBar({
    filters, statistics, open, onOpenChange, onChange, onClear,
}: {
    filters: DocumentFilters;
    statistics: DocumentStatistics;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onChange: (next: DocumentFilters) => void;
    onClear: () => void;
}) {
    const active = anyFilterActive(filters);

    const toggleIn = (set: Set<string>, value: string) => {
        const next = new Set(set);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        return next;
    };

    // WHAT IS FILTERED, IN WORDS, for the collapsed header. Statuses and types are listed because
    // they are short and specific; the recency controls are named rather than spelled out, since
    // "created in the last 14 days" is longer than the row it has to sit on.
    const summary = [
        ...Array.from(filters.statuses),
        ...Array.from(filters.types),
        ...(filters.newestOnly ? ["Newest"] : []),
        ...(filters.daysOldEnabled ? ["Days old"] : []),
    ].join(", ");

    return (
        <Box sx={{ borderBottom: `1px solid ${BORDER}`, px: 4, py: 1 }}>
            <Box sx={{ maxWidth: 1280, mx: "auto" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <IconButton size="small" onClick={() => onOpenChange(!open)}
                                aria-label={open ? "Hide filters" : "Show filters"}
                                aria-expanded={open}>
                        <ExpandMore sx={{ fontSize: 20, color: MUTED,
                                          transform: open ? "none" : "rotate(-90deg)",
                                          transition: "transform 150ms" }} />
                    </IconButton>
                    <Typography onClick={() => onOpenChange(!open)}
                                sx={{ fontSize: 11, fontWeight: 700, color: MUTED, letterSpacing: "0.5px",
                                      textTransform: "uppercase", cursor: "pointer" }}>
                        Filter By
                    </Typography>

                    {!open && active && (
                        <Typography sx={{ fontSize: 11, color: LINK, overflow: "hidden",
                                          textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {summary}
                        </Typography>
                    )}

                    <Box sx={{ flexGrow: 1 }} />

                    {/* Only offered when something is actually filtered -- a permanently visible
                        "Clear" that does nothing is noise. */}
                    {active && (
                        <Typography onClick={onClear}
                                    sx={{ fontSize: 11, color: LINK, cursor: "pointer",
                                          "&:hover": { textDecoration: "underline" } }}>
                            Clear
                        </Typography>
                    )}
                </Box>

                <Collapse in={open} unmountOnExit>
                    <Box sx={{ pb: 0.5 }}>
                        {/* Every lifecycle status reads as one list. The terminal three are the same
                            filter over the same column as the live ones, so they are not ruled off
                            into a section of their own. */}
                        <Section heading="Status">
                            {statistics.statuses.map(one => (
                                <FilterRow key={one.status} label={one.label} count={one.count}
                                           checked={filters.statuses.has(one.status)}
                                           onToggle={() => onChange({
                                               ...filters, statuses: toggleIn(filters.statuses, one.status),
                                           })} />
                            ))}
                        </Section>

                        {/* DRIVEN BY THE DATA, not by a flag passed in: a tenant whose documents are
                            all of one type renders no Type section rather than a heading with one
                            row in it that filters nothing.

                            FILTERED BY ACRONYM, not by USID -- the acronym is what the object number
                            is built from and what a person says out loud. */}
                        {statistics.types.length > 1 && (
                            <Section heading="Type">
                                {statistics.types.map(one => (
                                    // THE SEPARATOR IS PART OF THE STORED ACRONYM -- it is
                                    // "SOP-", because the object number is the acronym with the
                                    // sequence appended. Shown whole in a row of them it reads
                                    // "SOP- 9", which looks like a typo rather than a count; the
                                    // FILTER still uses the stored value, since that is what the
                                    // rows carry. Display only.
                                    <FilterRow key={one.acronym}
                                               label={one.acronym.replace(/[^A-Za-z0-9]+$/, "")}
                                               count={one.count}
                                               title={one.name || undefined}
                                               checked={filters.types.has(one.acronym)}
                                               onToggle={() => onChange({
                                                   ...filters, types: toggleIn(filters.types, one.acronym),
                                               })} />
                                ))}
                            </Section>
                        )}

                        {/* UNDER THEIR OWN HEADING, because these cut on the creation date rather
                            than on the lifecycle or the type -- sitting bare under the Status rows
                            made Newest read as a seventh status. "Other" is deliberately vague:
                            naming it "Date" would promise a date range this does not offer.

                            Uncounted, because the counts above are per status and would not narrow
                            with the date. */}
                        <Section heading="Other">
                            <FilterRow label="Newest" checked={filters.newestOnly}
                                       onToggle={() => onChange({ ...filters, newestOnly: !filters.newestOnly })} />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <Box onClick={() => onChange({ ...filters, daysOldEnabled: !filters.daysOldEnabled })}
                                     sx={{ cursor: "pointer", display: "flex" }}>
                                    <Checkbox checked={filters.daysOldEnabled} />
                                </Box>
                                {/* Typing in the box turns the filter on: reaching for the number and
                                    then having to tick the box as well reads as the control ignoring
                                    you. */}
                                <InputBase type="number" value={filters.daysOld}
                                           onChange={e => onChange({ ...filters, daysOld: e.target.value,
                                                                     daysOldEnabled: true })}
                                           onFocus={() => !filters.daysOldEnabled
                                               && onChange({ ...filters, daysOldEnabled: true })}
                                           inputProps={{ min: 1 }}
                                           sx={{ width: 44, fontSize: 11, border: `1px solid ${BORDER}`,
                                                 borderRadius: "2px", "& input": { p: "1px 4px", textAlign: "center" } }} />
                                <Typography onClick={() => onChange({ ...filters, daysOldEnabled: !filters.daysOldEnabled })}
                                            sx={{ fontSize: 11, cursor: "pointer", whiteSpace: "nowrap",
                                                  color: filters.daysOldEnabled ? LINK : TEXT,
                                                  fontWeight: filters.daysOldEnabled ? 700 : 400 }}>
                                    Days old
                                </Typography>
                            </Box>
                        </Section>
                    </Box>
                </Collapse>
            </Box>
        </Box>
    );
}
